import React, { useState } from 'react';
import {
  Box,
  Button,
  Heading,
  VStack,
  HStack,
  Input,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { Fade } from '@chakra-ui/transition';

interface PlayerSetupProps {
  onStart: (players: string[]) => void;
}

const MIN_PLAYERS = 2;
const MAX_PLAYERS = 6;

const PlayerSetup: React.FC<PlayerSetupProps> = ({ onStart }) => {
  const [names, setNames] = useState<string[]>(['', '']);
  const [touched, setTouched] = useState<boolean[]>([false, false]);

  const handleNameChange = (idx: number, value: string) => {
    const newNames = [...names];
    newNames[idx] = value;
    setNames(newNames);
  };

  const handleAddPlayer = () => {
    if (names.length < MAX_PLAYERS) {
      setNames([...names, '']);
      setTouched([...touched, false]);
    }
  };

  const handleRemovePlayer = (idx: number) => {
    if (names.length > MIN_PLAYERS) {
      setNames(names.filter((_, i) => i !== idx));
      setTouched(touched.filter((_, i) => i !== idx));
    }
  };

  const handleBlur = (idx: number) => {
    const newTouched = [...touched];
    newTouched[idx] = true;
    setTouched(newTouched);
  };

  const isValid = names.every((name) => name.trim().length > 0) && new Set(names.map(n => n.trim().toLowerCase())).size === names.length;

  const handleStart = () => {
    if (!isValid) {

      return;
    }
    onStart(names.map((n) => n.trim()));
  };

  return (
    <Fade in={true}>
      <Box maxW="md" mx="auto" mt={16} p={8} borderRadius="2xl" boxShadow="2xl" bgGradient="linear(to-br, teal.300, blue.200)">
        <Heading mb={6} textAlign="center" color="black" letterSpacing={2}>
          🎲 Tärning - Först till 10000 🎲
        </Heading>
        <VStack align="stretch">
          {names.map((name, idx) => (
            <HStack key={idx}>
              <Input
                placeholder={`Player ${idx + 1} name`}
                value={name}
                onChange={(e) => handleNameChange(idx, e.target.value)}
                onBlur={() => handleBlur(idx)}
                bg="whiteAlpha.900"
                borderColor={touched[idx] && !name.trim() ? 'red.400' : 'gray.200'}
                size="lg"
                fontWeight="bold"
                transition="all 0.2s"
                _focus={{ borderColor: 'teal.400', boxShadow: '0 0 0 2px #81e6d9' }}
              />
              {names.length > MIN_PLAYERS && (
                <IconButton
                  aria-label="Remove player"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleRemovePlayer(idx)}
                  size="sm"
                  _hover={{ bg: 'red.200' }}
                />
              )}
            </HStack>
          ))}
          <HStack justify="space-between" mt={2}>
            <Button
              colorScheme="teal"
              variant="outline"
              onClick={handleAddPlayer}
              _hover={{ bg: 'teal.100' }}
            >
              Add Player
            </Button>
            <Text color="whiteAlpha.800" fontSize="sm">
              {names.length} / {MAX_PLAYERS} players
            </Text>
          </HStack>
          <Button
            colorScheme="teal"
            size="lg"
            mt={6}
            onClick={handleStart}
            _hover={{
              bgGradient: 'linear(to-r, teal.400, blue.400)',
              color: 'white',
              transform: 'scale(1.05)',
            }}
            transition="all 0.2s"
            boxShadow="lg"
          >
            Start Game
          </Button>
        </VStack>
      </Box>
    </Fade>
  );
};

export default PlayerSetup;
